import { useEffect, useRef, useCallback } from 'react';
import { getAuth } from 'firebase/auth';
import { sendHeartbeat } from '../services/userService';
import { logger } from '../utils/logger';

// Configuration
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const MIN_ACTIVE_TIME_MS = 30 * 1000; // 30 seconds minimum to count

/**
 * Hook that tracks active time using Page Visibility API
 * Sends heartbeat every 5 minutes of active time
 */
export function useActivityTracker() {
  const activeTimeRef = useRef(0);
  const lastActiveRef = useRef(Date.now());
  const isVisibleRef = useRef(!document.hidden);
  const heartbeatTimerRef = useRef(null);

  // Send accumulated time to server
  const flushActiveTime = useCallback(async () => {
    const auth = getAuth();
    if (!auth.currentUser) {
      return;
    }

    const activeMinutes = Math.floor(activeTimeRef.current / 60000);
    if (activeMinutes > 0) {
      logger.info(`Flushing ${activeMinutes} minutes of active time`);
      await sendHeartbeat(activeMinutes);
      activeTimeRef.current = activeTimeRef.current % 60000; // Keep remainder
    }
  }, []);

  // Update active time based on visibility
  const updateActiveTime = useCallback(() => {
    if (isVisibleRef.current) {
      const now = Date.now();
      const elapsed = now - lastActiveRef.current;
      
      // Only count if reasonable (less than 10 minutes since last check)
      if (elapsed > 0 && elapsed < 10 * 60 * 1000) {
        activeTimeRef.current += elapsed;
      }
      lastActiveRef.current = now;
    }
  }, []);

  // Handle visibility change
  const handleVisibilityChange = useCallback(() => {
    const wasVisible = isVisibleRef.current;
    const isNowVisible = !document.hidden;
    
    if (wasVisible && !isNowVisible) {
      // Tab hidden - record time spent
      updateActiveTime();
      
      // Flush if we have enough time accumulated
      if (activeTimeRef.current >= MIN_ACTIVE_TIME_MS) {
        flushActiveTime();
      }
    } else if (!wasVisible && isNowVisible) {
      // Tab visible again - reset timer
      lastActiveRef.current = Date.now();
    }
    
    isVisibleRef.current = isNowVisible;
  }, [updateActiveTime, flushActiveTime]);

  // Handle page unload
  const handleBeforeUnload = useCallback(() => {
    updateActiveTime();
    
    // Use sendBeacon for reliable delivery on page close
    const auth = getAuth();
    if (auth.currentUser && activeTimeRef.current >= MIN_ACTIVE_TIME_MS) {
      const minutes = Math.floor(activeTimeRef.current / 60000);
      if (minutes > 0) {
        // Note: sendBeacon doesn't support auth headers well, 
        // so we flush before unload in the timer
        flushActiveTime();
      }
    }
  }, [updateActiveTime, flushActiveTime]);

  useEffect(() => {
    // Don't track if no user
    const auth = getAuth();
    if (!auth.currentUser) {
      return;
    }

    // Initialize
    lastActiveRef.current = Date.now();
    isVisibleRef.current = !document.hidden;

    // Set up periodic heartbeat
    heartbeatTimerRef.current = setInterval(() => {
      if (isVisibleRef.current) {
        updateActiveTime();
        flushActiveTime();
      }
    }, HEARTBEAT_INTERVAL_MS);

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Listen for page unload
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup
    return () => {
      // Flush remaining time on unmount
      updateActiveTime();
      flushActiveTime();

      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [updateActiveTime, flushActiveTime, handleVisibilityChange, handleBeforeUnload]);

  return null; // This hook doesn't render anything
}

export default useActivityTracker;
